import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import SEOHead from '../components/SEOHead';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ShareButtons from '../components/ShareButtons';
import { AnnonceBlogCTA } from '../components/AnnonceBlogCTA';
import TestimonialsBlogBlock from '../components/TestimonialsBlogBlock';
import { Button } from '../components/ui/button';
import { Calendar, Clock, ArrowLeft, Tag, Loader2, FileDown } from 'lucide-react';
import { articlesData } from '../data/articlesData';
import { logger } from '../lib/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlogArticle = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const response = await fetch(`${API_URL}/api/articles/${slug}`);
        if (response.ok) {
          const data = await response.json();
          setArticle({
            ...data,
            readTime: data.read_time
          });
          setLoading(false);
          return;
        }
      } catch (err) {
        logger.warn('API non disponible — fallback statique', err);
      }
      
      // Fallback to static data
      const staticArticle = articlesData[slug];
      if (staticArticle) {
        setArticle({ slug, ...staticArticle });
      } else {
        setNotFound(true);
      }
      setLoading(false);
    };

    fetchArticle();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1a1c1b] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#d9fb06] animate-spin" />
      </div>
    );
  }

  if (notFound || !article) {
    return (
      <div className="min-h-screen bg-[#1a1c1b] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-[#d9fb06] mb-4">Article non trouvé</h1>
          <Link to="/blog">
            <Button className="bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90 rounded-full px-8 py-3">
              Retour au blog
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title={`${article.title} | validatedboat`}
        description={article.excerpt}
        keywords={article.keywords}
        canonical={`https://validatedboat.fr/blog/${slug}`}
        image={article.image}
        type="article"
        article={{
          title: article.title,
          excerpt: article.excerpt,
          image: article.image,
          publishedDate: article.published_date || article.publishedDate,
          category: article.category
        }}
      />

      <div className="min-h-screen bg-[#1a1c1b]">
        <Header />
        
        {/* Hero Article */}
        <article className="pt-32 pb-16">
          <div className="max-w-4xl mx-auto px-5 md:px-10">
            {/* Back Button */}
            <Link to="/blog" className="inline-flex items-center space-x-2 text-[#d9fb06] hover:text-[#d9fb06]/80 mb-8 transition-colors">
              <ArrowLeft size={20} />
              <span className="font-semibold">Retour au blog</span>
            </Link>

            {/* Category */}
            <div className="flex items-center space-x-2 mb-4">
              <Tag className="text-[#d9fb06]" size={20} />
              <span className="text-[#d9fb06] font-semibold uppercase text-sm">{article.category}</span>
            </div>

            {/* Title */}
            <h1 className="text-4xl md:text-6xl font-black text-[#d9fb06] mb-6 leading-tight">
              {article.title}
            </h1>

            {/* Meta */}
            <div className="flex items-center space-x-6 text-[#888680] mb-8">
              <div className="flex items-center space-x-2">
                <Calendar size={18} />
                <span>{article.date}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Clock size={18} />
                <span>{article.readTime || article.read_time} de lecture</span>
              </div>
            </div>

            {/* Featured Image */}
            <div className="rounded-lg overflow-hidden mb-8">
              <img 
                src={article.image}
                alt={article.title}
                className="w-full h-[400px] object-cover"
              />
            </div>

            {/* Share Buttons (top) */}
            <ShareButtons
              url={`https://validatedboat.fr/blog/${slug}`}
              slug={slug}
              title={article.title}
              excerpt={article.excerpt}
            />

            {/* Content */}
            <div className="prose prose-invert prose-lg max-w-none">
              <div 
                className="article-content text-[#dfddd6] leading-relaxed space-y-6"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.content || '') }}
              />
            </div>

            {/* Mid-article CTA */}
            <AnnonceBlogCTA variant="mid" />

            {/* Testimonial cross-sell (renders only when at least one published review exists) */}
            <TestimonialsBlogBlock />

            {/* Share Buttons (bottom) */}
            <ShareButtons
              url={`https://validatedboat.fr/blog/${slug}`}
              slug={slug}
              title={article.title}
              excerpt={article.excerpt}
            />

            {/* PDF Download */}
            {article.pdf_url && (
              <div className="mt-12 mb-8 bg-[#302f2c] border border-[#3f4816] rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="bg-[#d9fb06] p-3 rounded-full">
                      <FileDown className="text-[#1a1c1b]" size={24} />
                    </div>
                    <div>
                      <h4 className="text-[#dfddd6] font-bold">Document à télécharger</h4>
                      <p className="text-[#888680] text-sm">{article.pdf_name || 'Document PDF'}</p>
                    </div>
                  </div>
                  <a
                    href={article.pdf_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                    className="bg-[#d9fb06] text-[#1a1c1b] px-6 py-3 rounded-full font-bold uppercase flex items-center gap-2 hover:bg-[#c4e205] transition-colors"
                  >
                    <FileDown size={18} />
                    Télécharger
                  </a>
                </div>
              </div>
            )}

            {/* CTA Section — pushes to /analyse-annonce-bateau */}
            <AnnonceBlogCTA variant="end" />
          </div>
        </article>

        <Footer />
      </div>
    </>
  );
};

export default BlogArticle;
